import { useMemo } from "react";

import Grid from "@mui/material/Grid";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useGetUserQuery } from "services/users";

import PureToneAudiogramLegend from "./pure-tone-audiogram-legend.component";
import PureToneAverage from "./pure-tone-average.component";
import PureToneGraph from "./pure-tone-graph.component";
import type { PureToneGraphViewProps } from "./pure-tone-graphs-area.component";
import { getGraphDataRow } from "./utils";

export default function PureToneGraphMergedView({
  earsData,
  hiddenData,
  displayLegend = true,
  sx = {},
  containerSx = {},
  pdfIdentifier = "",
  showOverlay,
  // No user preference backs the speech banana (it is opt-in per report), so
  // unlike showOverlay there is nothing to fall back to -- default it off.
  showSpeechBanana = false,
  hideUltraHigh,
  copyButtonTop,
  onDataUpdate
}: PureToneGraphViewProps) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("mac14"));
  const { left, right } = useMemo(() => {
    return {
      left: getGraphDataRow(
        earsData.left.airConduction,
        earsData.left.airConductionAnnotation
      ),
      right: getGraphDataRow(
        earsData.right.airConduction,
        earsData.right.airConductionAnnotation
      )
    };
  }, [
    earsData.left.airConduction,
    earsData.left.airConductionAnnotation,
    earsData.right.airConduction,
    earsData.right.airConductionAnnotation
  ]);

  const currentUser = useAppSelector(selectCurrentUser);
  const { data: user } = useGetUserQuery(currentUser.id, {
    skip: !currentUser.id || currentUser.id === -1
  });

  const switchAudiogramOrientation = Boolean(
    user?.user_preferences?.reporting?.switch_audiogram_orientation
  );

  const resolvedShowOverlay =
    showOverlay ??
    Boolean(user?.user_preferences?.reporting?.pure_tone_audiogram_overlay);

  return (
    <Grid
      data-testid="pure-tone-merged-graph"
      container
      alignItems="center"
      spacing={0}
      justifyContent="space-between"
      direction={isLargeScreen ? "row" : "column-reverse"}
      sx={{
        background: theme.palette.pureToneGraphMergedView.bgcolor,
        ...containerSx
      }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          winWidth: 460,
          minHeight: 450,
          mb: 3
        }}
      >
        {switchAudiogramOrientation ? (
          <PureToneAverage
            ear="right"
            airConductionValues={right}
            sx={{ mt: 4.2 }}
          />
        ) : (
          <PureToneAverage
            ear="left"
            airConductionValues={left}
            sx={{ mt: 4.2 }}
          />
        )}

        <PureToneGraph
          sx={{ ...sx }}
          label={
            switchAudiogramOrientation ? "Right and Left" : "Left and Right"
          }
          pdfIdentifier={pdfIdentifier}
          isMergedView
          showOverlay={resolvedShowOverlay}
          showSpeechBanana={showSpeechBanana}
          copyButtonTop={copyButtonTop}
          hideUltraHigh={hideUltraHigh}
          onDataUpdate={onDataUpdate}
          items={[
            {
              ear: "left",
              data: earsData.left,
              hiddenData: hiddenData.left
            },
            {
              ear: "right",
              data: earsData.right,
              hiddenData: hiddenData.right
            }
          ]}
        />
        {switchAudiogramOrientation ? (
          <PureToneAverage
            ear="left"
            airConductionValues={left}
            sx={{ mt: 4.2 }}
          />
        ) : (
          <PureToneAverage
            ear="right"
            airConductionValues={right}
            sx={{ mt: 4.2 }}
          />
        )}
      </Grid>

      {displayLegend && (
        <Grid item xs={12}>
          <PureToneAudiogramLegend />
        </Grid>
      )}
    </Grid>
  );
}
